import { Card, Column, Columns, Inline, Stack, Text, vars } from '@etg/wings';
import { useDateFormat, useTranslation, useSiteContext } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import { constants, date, duration as durationUtil } from '@eti/utils';
import { useBrandedFareName } from '../../../../../../../common/custom-hooks/useBrandedFareName';
import { getStopoverType } from '../../../../../../../utils/format';
import {
  getBoundTitle,
  getBoundType,
} from '../../../../../../../widgets/trip-details/utils/format';
import { ALL, SOME } from '../../../../../../../constants/transportationMeansConstants';
import { TripDetailsLocationTimeAndDate } from '../TripDetailsLocationTimeAndDate';
import { TripDetailsBoundCarrierLogo } from '../TripDetailsBoundCarrierLogo';
import { SelfTransferIndicator, StopoverInfoIndicator } from '../TripIndicator';
import { usetransformSegments } from '../../../../../../../widgets/cart/hooks/useCartTripDetailsSegment';
import type { TripDetailsBoundProps } from './model';
import {
  StyledAirportInline,
  InfoTextStyled,
  SeparatorStyled,
  tripDetailsLocationTimeAndDateWrapperCss,
} from './TripDetailsBoundSmall';
import { selfTransferIndicatorWrapper } from './TripDetailsBoundA';
import useGenerateTripBoundAriaLabel from './hooks/useGenerateTripBoundAriaLabel';

const tripTypeTextStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;

const sectionStyles = css`
  border-bottom: 0;
  overflow: hidden;
  padding: 8px 16px;

  :first-child,
  :last-child {
    margin: 0;
    padding: 8px 16px;
  }
`;

const marketingCarrierStyles = css`
  display: inline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 94px;

  @media ${mediaQueries.xlarge.up} {
    width: 140px;
  }
`;

const dotSeparatorStyles = css`
  background-color: #737373;
  border-radius: 2px;
  height: 3px;
  width: 3px;
`;

const cabinClassStyles = css`
  color: #737373;
`;

const airportStyles = css`
  width: 94px;
`;

const stackStyles = css`
  width: 156px;
`;

const nearbyAirportTextStyle = css`
  background: ${vars.colors.signal.caution.tint};
  color: ${vars.colors.signal.caution.dark};
  padding: 0 4px;
`;

const { unicodeControlCharacters } = constants;
const { LRE, PDF } = unicodeControlCharacters;
export const TripDetailsBoundB = ({
  boundAsSegment,
  boundNumber,
  flightId,
  includesTrainSegments,
  isMultiStop,
  marketingCarriers,
  segments,
  differentDestinationAirportCode,
  differentOriginAirportCode,
  isVISelfTransferDealEnabled,
}: TripDetailsBoundProps) => {
  const { t } = useTranslation();
  const { day } = useDateFormat();
  const { language, timeFormat } = useSiteContext();
  const {
    arrivedAt,
    brandedFareInformation,
    cabinClassName,
    departuredAt,
    destination,
    duration,
    marketingCarrier,
    origin,
    stops,
    isSelfTransfer,
  } = boundAsSegment;
  const brandedFareInfoAndOperatingCarrier = { brandedFareInformation, marketingCarrier };
  const brandedFareName = useBrandedFareName(brandedFareInfoAndOperatingCarrier);
  const stopoverType = getStopoverType(segments);
  const transformedSegments = segments && usetransformSegments(segments);
  const stoppageNames = transformedSegments
    ?.filter(({ isTripSegment }: any) => isTripSegment)
    .map(({ segment }: any) => segment.origin.airportCode ?? segment.origin.code);
  stoppageNames.shift();
  const title = t(getBoundTitle(getBoundType(boundNumber - 1), isMultiStop), boundNumber);
  const tripBoundAriaLabel = useGenerateTripBoundAriaLabel(
    boundAsSegment,
    marketingCarriers,
    title,
    includesTrainSegments,
  );
  const isDirectFlight = stops === 0;
  const stopText =
    stops === 1 ? t('TripDetails.Details.Stop') : t('TripDetails.Details.Stops', stops);

  const localizedDuration = durationUtil.getDuration(
    duration,
    language.code,
    timeFormat,
    ['h', 'm', 's'],
    false,
  );
  const durationInDays = date.durationInDays(new Date(departuredAt), new Date(arrivedAt));

  return (
    <Card.Section
      aria-label={tripBoundAriaLabel}
      className={sectionStyles}
      data-testid={`resultPage-resultTrip-${flightId}-bound-${boundNumber}-wrapper`}
      padding={4}
    >
      <Stack aria-hidden spacing={8}>
        <Inline alignY="center" spacing={8}>
          <div data-testid={`trip-bound-${boundNumber}-tripType`}>
            <div className={tripTypeTextStyles}>{title}</div>
          </div>
          <div data-testid={`trip-bound-${boundNumber}-info-date`}>
            {date.format(departuredAt, day)}
          </div>
          <div className={dotSeparatorStyles} />
          <div
            className={cabinClassStyles}
            data-testid={`trip-bound-${boundNumber}-cabinClass-info`}
          >
            {brandedFareName || t(`IbeClient.CabinClass.${cabinClassName}.Text`)}
          </div>
        </Inline>
        <Columns>
          <Column>
            <Stack className={stackStyles} spacing={4}>
              <Inline
                align="start"
                alignY="center"
                className={tripDetailsLocationTimeAndDateWrapperCss}
                spacing={4}
              >
                <TripDetailsLocationTimeAndDate
                  boundNumber={boundNumber}
                  dateAndTime={departuredAt}
                  differentAirportCode={differentOriginAirportCode}
                  type="departure"
                />

                <SeparatorStyled>&ndash;</SeparatorStyled>
                <TripDetailsLocationTimeAndDate
                  boundNumber={boundNumber}
                  dateAndTime={arrivedAt}
                  differentAirportCode={differentDestinationAirportCode}
                  durationInDays={durationInDays}
                  type="arrival"
                />
              </Inline>
              <StyledAirportInline>
                <Inline
                  alignY="center"
                  data-testid={`trip-bound-${boundNumber}-info-location`}
                  spacing={4}
                >
                  <Text
                    className={differentOriginAirportCode ? nearbyAirportTextStyle : ''}
                    data-testid={`trip-bound-${boundNumber}-info-departure-airportCode`}
                  >
                    {origin.airportCode ?? origin.code}
                  </Text>
                  <SeparatorStyled isLightSeparator>&ndash;</SeparatorStyled>
                  <Text
                    className={differentDestinationAirportCode ? nearbyAirportTextStyle : ''}
                    data-testid={`trip-bound-${boundNumber}-info-arrival-airportCode`}
                  >
                    {destination.airportCode ?? destination.code}
                  </Text>
                </Inline>
              </StyledAirportInline>
            </Stack>
          </Column>
          <Column>
            <Stack align="start" spacing={4}>
              <InfoTextStyled
                bold
                className={airportStyles}
                data-testid="searchResults-segment-stops"
              >
                {isDirectFlight ? t('TripDetails.Details.Direct') : stopText}
              </InfoTextStyled>
              {stoppageNames.length > 0 && (
                <StyledAirportInline
                  className={airportStyles}
                  data-testid={`trip-bound-${boundNumber}-info-stoppage`}
                >
                  {stoppageNames.join(', ')}
                </StyledAirportInline>
              )}
              {includesTrainSegments === SOME && (
                <StyledAirportInline className={airportStyles}>
                  {t('IbeClient.Segment.PartByTrain.Text')}
                </StyledAirportInline>
              )}
              {includesTrainSegments === ALL && (
                <StyledAirportInline className={airportStyles}>
                  {t('IbeClient.Segment.Train.Text')}
                </StyledAirportInline>
              )}
            </Stack>
          </Column>
          <Column>
            <Stack spacing={4}>
              <InfoTextStyled bold data-testid="searchResults-segment-duration">
                {`${LRE}${localizedDuration}${PDF}`}
              </InfoTextStyled>
              <StyledAirportInline className={marketingCarrierStyles}>
                {marketingCarriers.map(({ name }: { name: string }) => name).join(', ')}
              </StyledAirportInline>
            </Stack>
          </Column>
          <Column>
            <TripDetailsBoundCarrierLogo marketingCarriers={marketingCarriers} />
          </Column>
        </Columns>
        <Inline className={selfTransferIndicatorWrapper(marketingCarriers.length)} spacing={4}>
          {isSelfTransfer && !isVISelfTransferDealEnabled && (
            <SelfTransferIndicator boundNumber={boundNumber} />
          )}
          {stopoverType && <StopoverInfoIndicator stopoverType={stopoverType} />}
        </Inline>
      </Stack>
    </Card.Section>
  );
};
